import { motion } from "framer-motion";
import { 
  FileText, 
  CheckCircle2, 
  Building2, 
  Plane, 
  Lightbulb, 
  Users, 
  MoreHorizontal,
  Check
} from "lucide-react";

const SERVICE_OPTIONS = [
  { 
    value: "small_scale_smme", 
    label: "Small Scale (SMME)",
    icon: Building2,
  },
  { 
    value: "trade_mission_opportunities", 
    label: "Trade Mission Opportunities",
    icon: Plane,
  },
  { 
    value: "general_business_advisory", 
    label: "General Business Advisory/Assistance",
    icon: Lightbulb,
  },
  { 
    value: "networking_events", 
    label: "Networking & Events",
    icon: Users,
  },
  { 
    value: "other", 
    label: "Other (Specify)",
    icon: MoreHorizontal,
  },
];

export default function Step3DescriptionServices({ formData, setFormData, errors }) {
  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleServiceToggle = (serviceValue) => {
    setFormData((prev) => {
      const currentServices = prev.selectedServices || [];
      const isSelected = currentServices.includes(serviceValue);

      if (isSelected) {
        // Remove service
        const updatedServices = currentServices.filter((s) => s !== serviceValue);
        // If "other" is deselected, clear the other specification
        if (serviceValue === "other") {
          return {
            ...prev,
            selectedServices: updatedServices,
            otherServiceSpecification: "",
          };
        }
        return {
          ...prev,
          selectedServices: updatedServices,
        };
      } else {
        // Add service
        return {
          ...prev,
          selectedServices: [...currentServices, serviceValue],
        };
      }
    });
  };

  const isServiceSelected = (serviceValue) => {
    return (formData.selectedServices || []).includes(serviceValue);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="p-8 bg-white rounded-xl border border-gray-200 shadow-sm"
    >
      {/* Section Title */}
      <div className="flex gap-3 items-center mb-8">
        <div className="flex justify-center items-center w-10 h-10 text-white bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-md">
          <FileText size={20} />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">
          Descriptions & Services of your Company
        </h2>
      </div>

      {/* Company Description */}
      <div className="mb-8">
        <label className="block mb-2 text-sm font-semibold text-gray-700">
          Please provide a brief description of your company{" "}
          <span className="text-red-500">*</span>
        </label>
        <textarea
          value={formData.companyDescription || ""}
          onChange={(e) => handleChange("companyDescription", e.target.value)}
          rows={6}
          className={`w-full px-4 py-3 text-gray-900 bg-white rounded-lg border transition-all outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none ${
            errors?.companyDescription
              ? "border-red-300 bg-red-50"
              : "border-gray-300 hover:border-gray-400"
          }`}
          placeholder="Enter a detailed description of your company, its mission, values, and what makes it unique..."
        />
        {errors?.companyDescription && (
          <p className="mt-1 text-sm text-red-500">{errors.companyDescription}</p>
        )}
      </div>

      {/* Services Section */}
      <div>
        <div className="flex gap-3 items-center mb-4">
          <div className="flex justify-center items-center w-8 h-8 text-orange-600 bg-orange-50 rounded-lg">
            <CheckCircle2 size={16} />
          </div>
          <h3 className="text-lg font-semibold text-gray-800">
            Services - Please select your interest:
          </h3>
        </div>
        <p className="mb-6 text-sm text-gray-600">
          Please tick your interest
        </p>

        {/* Service Options - Horizontal Card Layout */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICE_OPTIONS.map((option, index) => {
            const isSelected = isServiceSelected(option.value);
            const IconComponent = option.icon;

            return (
              <motion.div
                key={option.value}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -4 }}
                whileTap={{ scale: 0.98 }}
              >
                <button
                  type="button"
                  onClick={() => handleServiceToggle(option.value)}
                  className={`relative w-full p-4 text-left bg-white rounded-lg border-2 transition-all duration-300 cursor-pointer group ${
                    isSelected
                      ? "text-white bg-gradient-to-br from-orange-500 to-orange-600 border-orange-500 shadow-lg"
                      : "border-gray-200 hover:border-orange-300 hover:shadow-md"
                  }`}
                >
                  {/* Selection Indicator */}
                  {isSelected && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute top-2 right-2"
                    >
                      <div className="flex justify-center items-center w-5 h-5 text-white rounded-full backdrop-blur-sm bg-white/20">
                        <Check size={12} />
                      </div>
                    </motion.div>
                  )}

                  {/* Icon */}
                  <div
                    className={`flex justify-center items-center w-10 h-10 mb-3 rounded-lg transition-all duration-300 ${
                      isSelected
                        ? "backdrop-blur-sm bg-white/20"
                        : "bg-orange-50 group-hover:scale-110"
                    }`}
                  >
                    <IconComponent
                      size={20}
                      className={
                        isSelected ? "text-white" : "text-orange-600"
                      }
                    />
                  </div>

                  {/* Label */}
                  <h4
                    className={`text-sm font-semibold leading-tight transition-colors ${
                      isSelected ? "text-white" : "text-gray-900"
                    }`}
                  >
                    {option.label}
                  </h4>

                  {/* Hover Effect Overlay */}
                  {!isSelected && (
                    <div className="absolute inset-0 bg-gradient-to-br rounded-xl transition-all duration-300 from-orange-500/0 to-orange-500/0 group-hover:from-orange-500/5 group-hover:to-orange-500/10" />
                  )}
                </button>
              </motion.div>
            );
          })}
        </div>

        {/* Other Specification Input - Full Width Below Cards */}
        {isServiceSelected("other") && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-6"
          >
            <label className="block mb-2 text-sm font-semibold text-gray-700">
              Please specify your other service interest{" "}
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.otherServiceSpecification || ""}
              onChange={(e) =>
                handleChange("otherServiceSpecification", e.target.value)
              }
              className={`w-full px-4 py-3 text-gray-900 bg-white rounded-lg border transition-all outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                errors?.otherServiceSpecification
                  ? "border-red-300 bg-red-50"
                  : "border-gray-300 hover:border-gray-400"
              }`}
              placeholder="Please specify your other service interest..."
            />
            {errors?.otherServiceSpecification && (
              <p className="mt-1 text-sm text-red-500">
                {errors.otherServiceSpecification}
              </p>
            )}
          </motion.div>
        )}

        {errors?.selectedServices && (
          <p className="mt-4 text-sm text-red-500">{errors.selectedServices}</p>
        )}
      </div>
    </motion.div>
  );
}

